import React, { ComponentType, ContextType } from 'react';
import hoistNonReactStatics, { NonReactStatics } from 'hoist-non-react-statics';
import { ReducersMapObject } from 'redux';
import { StoreContext, StoreEnvOptions } from './getStoreEnvOptions';
import { getReducerInjectors } from './getReducerInjectors';

/**
 * Create injector of reducer
 * @name createReducerInjector
 * @function
 *
 */
export const createReducerInjector = ({ context }: StoreEnvOptions) =>
  /**
   * Dynamically injects a reducer
   *
   * @name injectReducers
   * @function
   * @param {object} reducers - a key/value map of reducers, where `key` - a key of the reducer inside of store, `reducer` - a reducer that will be injected by key
   *
   */
  <T extends ReducersMapObject>(
    reducers: T extends Record<string, never> ? never : T,
  ) => <TProps extends object>(
    WrappedComponent: ComponentType<TProps>,
  ): ComponentType<TProps> & NonReactStatics<ComponentType<TProps>> => {
    class ReducerInjector extends React.Component {
      static displayName = `withReducer(${
        WrappedComponent.displayName || WrappedComponent.name || 'Component'
      })`;

      static WrappedComponent = WrappedComponent;

      static contextType = context;

      declare context: ContextType<StoreContext>;

      declare props: Readonly<TProps>;

      injectors = getReducerInjectors(this.context.store);

      // eslint-disable-next-line camelcase
      UNSAFE_componentWillMount() {
        const { injectReducers: injectReducersInternal } = this.injectors;

        injectReducersInternal(reducers);
      }

      render() {
        return <WrappedComponent {...this.props} />;
      }
    }

    return hoistNonReactStatics(ReducerInjector, WrappedComponent);
  };
